import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const SubscriptionTrialContext = createContext();

// FIX: Moved VALID_TIERS outside the component to fix the dependency error
const VALID_TIERS = ['negocio', 'compania', 'corporacion'];

export const useSubscriptionTrial = () => {
    const context = useContext(SubscriptionTrialContext);
    if (!context) {
        throw new Error('useSubscriptionTrial must be used within a SubscriptionTrialProvider');
    }
    return context;
};

export const SubscriptionTrialProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { makeAuthenticatedRequest, loginType } = useAuth();

    // API base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Determine the correct base URL based on login type
    const getServiceBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/subscription-trials`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/subscription-trials`;
        } else {
            throw new Error('Invalid login type for subscription trial management');
        }
    }, [API_BASE_URL, loginType]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const data = await response.json();

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(data.error || 'Subscription or Business not found');
            } else if (response.status === 400) {
                throw new Error(data.error || 'Invalid request data');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error');
            }
            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate admin/master access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Only admin and master users can manage subscription trials');
        }
    }, [loginType]);

    // -------------------------------------------------------------------------
    // GIVE TRIAL
    // -------------------------------------------------------------------------
    // VALID_TIERS is now global, so we don't need to add it to the dependency array
    const giveTrial = useCallback(async (businessRegistryNumber, trialDays, tier) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (!trialDays || trialDays <= 0) throw new Error('Trial days must be a positive number');
        if (!tier) throw new Error('Tier is required');

        // Normalize tier for validation check
        const normalizedTier = tier.toLowerCase();
        
        // Simple client-side validation for known tiers (though backend handles variations)
        const validTierBase = VALID_TIERS.some(t => normalizedTier.includes(t) || normalizedTier.includes('small') || normalizedTier.includes('company'));
        if (!validTierBase) {
             console.warn("Warning: Unrecognized tier name format, proceeding but backend may reject.");
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber,
                trial_days: parseInt(trialDays, 10),
                tier: tier
            };

            const response = await makeAuthenticatedRequest(`${baseUrl}/trial/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to grant trial';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // CANCEL TRIAL
    // -------------------------------------------------------------------------
    const cancelTrial = useCallback(async (businessRegistryNumber) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber
            };

            const response = await makeAuthenticatedRequest(`${baseUrl}/trial/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to cancel trial';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // UTILITY & DEBUG
    // -------------------------------------------------------------------------

    const getEndpointInfo = useCallback(() => {
        return {
            loginType: loginType || 'none',
            managementUrl: loginType === 'master'
                ? `${API_BASE_URL}/master/subscription-trials`
                : loginType === 'admin'
                    ? `${API_BASE_URL}/admin/subscription-trials`
                    : 'N/A (requires login)',
            validTiers: VALID_TIERS
        };
    }, [loginType, API_BASE_URL]);

    const value = {
        // State
        loading,
        error,
        clearError,

        // Actions
        giveTrial,
        cancelTrial,

        // Utilities
        getEndpointInfo,
        VALID_TIERS,

        // Permissions
        hasManagementAccess: loginType === 'admin' || loginType === 'master',
    };

    return (
        <SubscriptionTrialContext.Provider value={value}>
            {children}
        </SubscriptionTrialContext.Provider>
    );
};